// src/components/DesignJournal.js

import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase-config";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import "../style/DesignJournal.css";

const POSTS_PER_PAGE = 3;
const MAX_CONTENT_LENGTH = 200;

// Sub-component for post content with Read more functionality
const PostContent = ({ content }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = content.length > MAX_CONTENT_LENGTH;

  return (
    <div className="dj-post-content">
      <p className="dj-post-text">
        {shouldTruncate && !isExpanded
          ? `${content.substring(0, MAX_CONTENT_LENGTH)}...`
          : content}
      </p>
      {shouldTruncate && (
        <span
          className="dj-read-more"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "Show less" : "Read more..."}
        </span>
      )}
    </div>
  );
};

// Sub-component for Image Carousel
const ImageCarousel = ({ post }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const imageContainerRef = useRef(null);
  const imageRefs = useRef([]);

  // Check if desktop on resize
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // IntersectionObserver for BOTH mobile and desktop
  useEffect(() => {
    if (!imageContainerRef.current || post.images.length <= 1) return;

    const options = {
      root: imageContainerRef.current,
      rootMargin: "0px",
      threshold: 0.5,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.getAttribute("data-index"), 10);
          setCurrentIndex(index);
        }
      });
    }, options);

    imageRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      imageRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [post.images.length]);

  // Handle pagination/dot click - scroll to that image
  const handlePaginationClick = (index) => {
    setCurrentIndex(index);
    if (imageRefs.current[index]) {
      imageRefs.current[index].scrollIntoView({ behavior: "smooth" });
    }
  };

  // MOBILE VIEW - matches FullProduct structure
  if (!isDesktop) {
    return (
      <div className="dj-carousel-mobile">
        <div className="dj-item-slider" ref={imageContainerRef}>
          <div className="dj-item-product-wrapper">
            {post.images.map((image, index) => (
              <div
                key={index}
                className="dj-item-product-container"
                ref={(el) => (imageRefs.current[index] = el)}
                data-index={index}
              >
                <img
                  src={image}
                  alt={`${post.title || "Design Journal"} - Image ${index + 1}`}
                  className="dj-carousel-image"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        {post.images.length > 1 && (
          <div className="dj-carousel-dots">
            {post.images.map((_, imgIndex) => (
              <span
                key={imgIndex}
                className={`dj-carousel-dot ${imgIndex === currentIndex ? "active" : ""}`}
                onClick={() => handlePaginationClick(imgIndex)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // DESKTOP VIEW - vertical scroll with thumbnails
  return (
    <div className="dj-carousel-wrapper">
      {/* Thumbnails Column */}
      <div className="dj-thumbnails-column">
        {post.images.map((image, index) => (
          <div
            key={index}
            className={`dj-thumbnail-indicator ${index === currentIndex ? "active" : ""}`}
            onClick={() => handlePaginationClick(index)}
          >
            <div className="dj-thumbnail-wrapper">
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="dj-thumbnail-image"
              />
            </div>
          </div>
        ))}
        {/* Vertical Ruler */}
        <div className="dj-vertical-ruler">
          {post.images.map((_, index) => (
            <div
              key={index}
              className={`dj-vertical-ruler-section ${index === currentIndex ? "highlighted" : ""}`}
              style={{
                top: `${(index / post.images.length) * 100}%`,
                height: `${100 / post.images.length}%`,
              }}
            ></div>
          ))}
        </div>
      </div>

      {/* Scrollable Images Container */}
      <div className="dj-carousel">
        <div className="dj-image-slider" ref={imageContainerRef}>
          <div className="dj-image-wrapper">
            {post.images.map((image, index) => (
              <div
                key={index}
                className="dj-image-container"
                ref={(el) => (imageRefs.current[index] = el)}
                data-index={index}
              >
                <img
                  src={image}
                  alt={`${post.title || "Design Journal"} - Image ${index + 1}`}
                  className="dj-carousel-image"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const DesignJournal = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setIsLoading(true);
        const postsCollection = collection(db, "designJournal");
        const q = query(postsCollection, orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);

        const fetchedPosts = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setPosts(fetchedPosts);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching design journal posts:", error);
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return "--/--/----";
    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      if (isNaN(date.getTime())) return "--/--/----";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return "--/--/----";
    }
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + POSTS_PER_PAGE, posts.length));
  };

  if (isLoading) {
    return (
      <div className="dj-container">
        <div className="dj-header">
          <h2>{t("Design Journal")}</h2>
        </div>
        <div className="dj-loading">
          <div className="dj-spinner"></div>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  const totalPosts = posts.length;
  const visiblePosts = posts.slice(0, visibleCount);
  const hasMorePosts = visibleCount < totalPosts;

  return (
    <div className="dj-container">
      <div className="dj-header">
        <h2>{t("Design Journal")}</h2>
      </div>

      {visiblePosts.map((post, index) => {
        const memoNumber = totalPosts - index;
        const hasImages = post.images && post.images.length > 0;

        return (
          <div
            key={post.id}
            className={`dj-post ${hasImages ? "dj-post-with-images" : "dj-post-no-images"}`}
          >
            <div className="dj-post-memo">
              <span>memo #{memoNumber}</span>
              <span>{formatDate(post.createdAt)}</span>
            </div>

            <div className="dj-post-layout">
              {hasImages && (
                <div className="dj-post-left">
                  <ImageCarousel post={post} />
                </div>
              )}

              <div
                className={`dj-post-right ${!hasImages ? "dj-post-right-full" : ""}`}
              >
                {post.title && <h3 className="dj-post-title">{post.title}</h3>}
                {post.content && <PostContent content={post.content} />}
              </div>
            </div>
          </div>
        );
      })}

      <div className="dj-navigation">
        <span
          className={`dj-load-more ${!hasMorePosts ? "disabled" : ""}`}
          onClick={hasMorePosts ? handleLoadMore : undefined}
        >
          {t("Load More")} | {visibleCount} {t("of")} {totalPosts}
        </span>
      </div>
    </div>
  );
};

export default DesignJournal;
